import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// VoiceDictationButton — uses the browser's Web Speech API for free,
// zero-backend speech-to-text. Appends transcript to the current value of
// any controlled <textarea>/<input>, so users can mix typing + dictation.
//
// Locale support: pass `langCode` (e.g. "en", "hi", "te", "ta"). We map to
// BCP-47 tags that Web Speech recognises well on Android Chrome / iOS Safari.
// ---------------------------------------------------------------------------
const LOCALE_MAP = {
  en: "en-IN",
  hi: "hi-IN",
  te: "te-IN",
  ta: "ta-IN",
  kn: "kn-IN",
  ml: "ml-IN",
  bn: "bn-IN",
  mr: "mr-IN",
  gu: "gu-IN",
  pa: "pa-IN",
  or: "or-IN",
  ur: "ur-IN",
};

// eslint-disable-next-line no-unused-vars
const Recognition = (typeof window !== "undefined") &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function VoiceDictationButton({ value, onAppend, langCode = "en", className = "", testId = "voice-dictate-btn" }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const recogRef = useRef(null);
  const baseRef = useRef("");  // text in box when the current session started

  useEffect(() => {
    const R = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!R) { setSupported(false); return; }
    const r = new R();
    r.continuous = true;
    r.interimResults = true;
    r.lang = LOCALE_MAP[langCode] || "en-IN";
    r.onresult = (e) => {
      let finalTxt = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTxt += chunk;
        else interim += chunk;
      }
      // Build updated value from base + accumulated final + current interim
      const prev = baseRef.current;
      const sep = prev && !/\s$/.test(prev) ? " " : "";
      if (finalTxt) {
        baseRef.current = prev + sep + finalTxt.trim();
        onAppend(baseRef.current);
      } else if (interim) {
        onAppend(prev + sep + interim);
      }
    };
    r.onerror = (e) => {
      const msg = e.error === "not-allowed"
        ? "Microphone access denied. Enable it in browser settings."
        : e.error === "no-speech"
          ? "Didn't catch that. Please try speaking again."
          : `Voice input error: ${e.error}`;
      toast.error(msg);
      setListening(false);
    };
    r.onend = () => setListening(false);
    recogRef.current = r;
    return () => { try { r.stop(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCode]);

  const toggle = () => {
    if (!supported) {
      toast.error("Voice input is not supported on this browser. Please use Chrome or Safari.");
      return;
    }
    const r = recogRef.current;
    if (!r) return;
    if (listening) {
      try { r.stop(); } catch {}
      setListening(false);
      return;
    }
    baseRef.current = value || "";
    try {
      r.start();
      setListening(true);
      toast.success("Listening… Speak now.");
    } catch (e) {
      toast.error("Could not start mic. Refresh and allow mic access.");
    }
  };

  if (!supported) {
    // Render nothing — fallback is typing.
    return null;
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={listening ? "Stop listening" : "Tap to speak"}
      data-testid={testId}
      className={
        `inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border transition ` +
        (listening
          ? "bg-red-50 border-red-300 text-red-700 animate-pulse"
          : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50") +
        " " + className
      }
    >
      {listening ? (
        <>
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
          </span>
          Listening…
          <MicOff className="w-4 h-4" />
        </>
      ) : (
        <>
          <Mic className="w-4 h-4" />
          Tap to speak
        </>
      )}
    </button>
  );
}
